import { getAppRoleLabel } from '../../Utils/appRoleLabels';
import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { appColors } from '../../Config/Theme';
import {
  ManagedRoleAssignmentInput,
  UserManagementCapabilities,
  UserManagementLocationOption,
  UserManagementRoleAssignment,
  UserManagementRoleOption,
} from '../../Types/UserManagement';

export interface RoleAssignmentDraft {
  selected: boolean;
  scope: 'global' | 'locations';
  locationIds: string[];
}

export type RoleAssignmentDrafts = Record<string, RoleAssignmentDraft>;

export const createRoleAssignmentDrafts = (
  roles: UserManagementRoleOption[],
  assignments: UserManagementRoleAssignment[] = [],
): RoleAssignmentDrafts =>
  roles.reduce<RoleAssignmentDrafts>((result, role) => {
    const existing = assignments.filter(
      (assignment) => assignment.appRoleId === role.appRoleId && assignment.canManage,
    );
    const hasGlobal = existing.some((assignment) => assignment.locationId == null);
    result[role.appRoleId] = {
      selected: existing.length > 0,
      scope: hasGlobal || role.requiresGlobalScope ? 'global' : 'locations',
      locationIds: existing
        .map((assignment) => assignment.locationId)
        .filter((locationId): locationId is string => Boolean(locationId)),
    };
    return result;
  }, {});

export const buildRoleAssignmentInputs = (
  roles: UserManagementRoleOption[],
  drafts: RoleAssignmentDrafts,
): ManagedRoleAssignmentInput[] =>
  roles.flatMap<ManagedRoleAssignmentInput>((role) => {
    const draft = drafts[role.appRoleId];
    if (!role.canAssign || !draft?.selected) return [];
    if (role.requiresGlobalScope || draft.scope === 'global') {
      return [{ appRoleId: role.appRoleId, locationId: null }];
    }
    return draft.locationIds.map((locationId) => ({
      appRoleId: role.appRoleId,
      locationId,
    }));
  });

interface RoleAssignmentEditorProps {
  roles: UserManagementRoleOption[];
  locations: UserManagementLocationOption[];
  capabilities: UserManagementCapabilities;
  drafts: RoleAssignmentDrafts;
  onChange: (drafts: RoleAssignmentDrafts) => void;
  disabled?: boolean;
}

const RoleAssignmentEditor: React.FC<RoleAssignmentEditorProps> = ({
  roles,
  locations,
  capabilities,
  drafts,
  onChange,
  disabled = false,
}) => {
  const update = (roleId: string, patch: Partial<RoleAssignmentDraft>) => {
    const current = drafts[roleId] ?? {
      selected: false,
      scope: capabilities.canManageGlobal ? 'global' : 'locations',
      locationIds: [],
    };
    onChange({ ...drafts, [roleId]: { ...current, ...patch } });
  };

  const handleLocations = (roleId: string, event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    update(roleId, { locationIds: typeof value === 'string' ? value.split(',') : value });
  };

  const assignableRoles = roles.filter((role) => role.canAssign);
  if (assignableRoles.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: appColors.text.caption }}>
        No roles are assignable within your current access scope.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {assignableRoles.map((role) => {
        const draft = drafts[role.appRoleId] ?? {
          selected: false,
          scope: capabilities.canManageGlobal ? 'global' as const : 'locations' as const,
          locationIds: [],
        };
        const canChooseGlobal = capabilities.canManageGlobal && !role.requiresGlobalScope;
        const locationScope = !role.requiresGlobalScope && draft.scope === 'locations';

        return (
          <Box
            key={role.appRoleId}
            sx={{
              border: `1px solid ${draft.selected ? appColors.primary.main : appColors.border.default}`,
              borderRadius: '10px',
              px: 1.5,
              py: 1.25,
              bgcolor: draft.selected ? 'rgba(46, 139, 132, 0.04)' : appColors.surface.card,
            }}
          >
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  checked={draft.selected}
                  onChange={(event) => update(role.appRoleId, { selected: event.target.checked })}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
                    {getAppRoleLabel(role.roleName)}
                  </Typography>
                  {role.roleDescription && (
                    <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                      {role.roleDescription}
                    </Typography>
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
            />

            {draft.selected && (
              <Box sx={{ pl: 4, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {role.requiresGlobalScope ? (
                  <Chip
                    label="Global access"
                    size="small"
                    sx={{ alignSelf: 'flex-start', bgcolor: '#EDE9FE', color: '#5B21B6' }}
                  />
                ) : (
                  <>
                    {canChooseGlobal && (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={draft.scope}
                          disabled={disabled}
                          onChange={(event) =>
                            update(role.appRoleId, {
                              scope: event.target.value as 'global' | 'locations',
                            })
                          }
                          inputProps={{ 'aria-label': `${getAppRoleLabel(role.roleName)} scope` }}
                        >
                          <MenuItem value="global">All locations (global)</MenuItem>
                          <MenuItem value="locations">Selected locations</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {locationScope && (
                      <FormControl size="small" fullWidth>
                        <Select
                          multiple
                          displayEmpty
                          value={draft.locationIds}
                          disabled={disabled}
                          onChange={(event) => handleLocations(role.appRoleId, event)}
                          input={<OutlinedInput />}
                          MenuProps={{
                            slotProps: {
                              paper: {
                                sx: {
                                  mt: 0.5,
                                  maxHeight: 360,
                                  borderRadius: '8px',
                                  border: `1px solid ${appColors.border.default}`,
                                },
                              },
                              list: {
                                sx: { py: 0 },
                              },
                            },
                          }}
                          renderValue={(selected) =>
                            selected.length === 0
                              ? 'Select locations'
                              : locations
                                  .filter((location) => selected.includes(location.locationId))
                                  .map((location) => location.locationName)
                                  .join(', ')
                          }
                          inputProps={{ 'aria-label': `${getAppRoleLabel(role.roleName)} locations` }}
                        >
                          {locations.map((location) => (
                            <MenuItem
                              key={location.locationId}
                              value={location.locationId}
                              sx={{
                                minHeight: 44,
                                px: 1.5,
                                py: 0.75,
                                gap: 1,
                                alignItems: 'center',
                                '&:not(:last-of-type)': {
                                  borderBottom: `1px solid ${appColors.border.subtle}`,
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={draft.locationIds.includes(location.locationId)}
                                sx={{ p: 0.5, flexShrink: 0 }}
                              />
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ color: appColors.text.heading, fontWeight: 400 }}
                              >
                                {location.locationName}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default RoleAssignmentEditor;
